/**
 * PropertyTab: identity essentials + public listing in one tab.
 *
 * Replaces the previous separate Overview and Directory tabs. Two
 * stacked sections sharing the same form state on the parent modal:
 *
 *   1. Essentials (Overview content)
 *      Identity, capacity, location, linked accounts, danger zone.
 *      Always required to keep a property well-formed.
 *
 *   2. Listing (Directory content)
 *      Public listing fields shown on the Verify page and in the
 *      Charter Suite. Optional and fallback-safe: leave blank and
 *      the property still publishes with tier defaults. Mirrors the
 *      Charter Suite "Listing" page the GM edits, so admin and GM
 *      are editing the same canonical fields.
 *
 * Renders the existing OverviewTab + DirectoryTab inline. Both
 * sub-components keep their save logic and field handling untouched;
 * this tab is a layout container, not a rewrite.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import OverviewTab from './OverviewTab';
import DirectoryTab from './DirectoryTab';
import { POS_GOLD } from '../constants';
import type { EnrichedProperty, PropertyEditForm, PropertyMode } from './types';

interface PropertyTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty | null;
  seededCustomer?: EnrichedProperty['customer'] | null;
  form: PropertyEditForm;
  setForm: (updater: (f: PropertyEditForm) => PropertyEditForm) => void;
  mode: PropertyMode;
  deleting?: boolean;
  onDelete?: () => Promise<boolean>;
  onAfterDelete?: () => void;
  canDelete?: boolean;
}

export default function PropertyTab({
  supabase,
  property,
  seededCustomer,
  form,
  setForm,
  mode,
  deleting = false,
  onDelete,
  onAfterDelete,
  canDelete = false,
}: PropertyTabProps) {
  return (
    <div>
      <OverviewTab
        supabase={supabase}
        property={property}
        seededCustomer={seededCustomer}
        form={form}
        setForm={setForm}
        mode={mode}
        deleting={deleting}
        onDelete={onDelete}
        onAfterDelete={onAfterDelete}
        canDelete={canDelete}
      />

      {/* Listing section. Only shown for existing properties since the
          public listing fields don't make sense until the property has
          been created. */}
      {mode !== 'create' && property && (
        <div style={{ marginTop: '2.5rem' }}>
          <div
            style={{
              borderTop: '1px solid var(--color-border, #e5e7eb)',
              paddingTop: '1.5rem',
              marginBottom: '1.25rem',
            }}
          >
            <h3
              className="section-title-sm"
              style={{ color: POS_GOLD, marginBottom: '0.375rem' }}
            >
              Listing (public Verify page + Charter Suite)
            </h3>
            <p className="text-muted fs-sm m-0">
              Optional. Anything left blank publishes with the tier defaults.
              Mirrors the listing page the GM sees in their Charter Suite.
            </p>
          </div>
          <DirectoryTab
            supabase={supabase}
            property={property}
            form={form}
            setForm={setForm}
            mode={mode}
          />
        </div>
      )}
    </div>
  );
}
